import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaWallet,
  FaSignOutAlt,
  FaFileDownload,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { squidloansUserAPI } from "../../services/api";
import { useAuth } from "../../Context/AuthContext";
import { formatDate } from "../../Utils/formatDate";
import { initiateRazorpayPayment } from "../../Utils/razorpay";
import { automationNodeLeadStageToRouteMap, LEAD_STAGE } from "../../Utils/constants";
import { financialTips } from "../../Utils/financialTips";
import Toast from "../../components/Toast";
import GiftSuccessModal from "../../components/GiftSuccessModal";
import PaymentSuccessModal from "../../components/PaymentSuccessModal";
import useLeadStage from "../../hooks/useLeadStage";

const curr_page_lead_stage = [
  LEAD_STAGE.DISBURSAL_INI,
  LEAD_STAGE.CLOSED,
  LEAD_STAGE.DISBURSED,
  LEAD_STAGE.PARTIAL_PAYMENT,
];

function Dashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { leadStage, isLoadingStage, isDisbursed } = useLeadStage();
  const [currentTip, setCurrentTip] = useState(0);
  const [dashboardData, setDashboardData] = useState({
    userName: "User",
    creditScore: 0,
  });
  const [loanHistory, setLoanHistory] = useState([]);
  const [isReloanLoading, setIsReloanLoading] = useState(false);
  const [showGiftSuccessModal, setShowGiftSuccessModal] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "error",
  });
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const showToast = (message, type = "warning") => {
    setToast({ show: true, message, type });
  };
  const hideToast = () => {
    setToast({ show: false, message: "", type: "error" });
  };
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  useEffect(() => {
    if (isLoadingStage) return;

    if (leadStage && !curr_page_lead_stage.includes(leadStage)) {
      navigate(automationNodeLeadStageToRouteMap[leadStage]);
      return;
    }
  }, [leadStage, navigate, isLoadingStage]);

  useEffect(() => {
    // Generate random tip initially
    setCurrentTip(Math.floor(Math.random() * financialTips.length));

    // Change tip every 24 hours
    const interval = setInterval(() => {
      setCurrentTip(Math.floor(Math.random() * financialTips.length));
    }, 24 * 60 * 60 * 1000);

    // Fetch loan status data
    const fetchLoanStatus = async () => {
      try {
        const response = await squidloansUserAPI.loanHistory();
        if (response && response.data) {
          setLoanHistory(response.data);

          // Get user info from the first loan (assuming same user for all loans)
          const firstLoan = response.data[0];
          if (firstLoan) {
            setDashboardData((prevData) => ({
              ...prevData,
              userName: firstLoan?.full_name,
              creditScore: firstLoan?.credit_score,
            }));

            // Repay/transfer modal eligibility is determined in useEffect below using isDisbursed
          }
        }
      } catch (error) {
        showToast(error.message);
      }
    };
    fetchLoanStatus();

    return () => {
      clearInterval(interval);
    };
  }, []);

  // Show repay or transfer modal based on is_disbursed from get journey
  useEffect(() => {
    if (isLoadingStage || loanHistory.length === 0) return;

    const firstLoan = loanHistory[0];
    if (!firstLoan) return;

    if (isDisbursed) {
      // Repay modal: show when current date <= repayment date and loan not closed
      if (firstLoan?.sanction_details?.repayment_date && !firstLoan.is_closed) {
        const repaymentDate = new Date(firstLoan.sanction_details.repayment_date);
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);
        repaymentDate.setHours(0, 0, 0, 0);
        if (currentDate <= repaymentDate) {
          setShowCouponModal(true);
        }
      }
    } else {
      // Transfer modal: show when amount not yet disbursed (user awaiting bank transfer)
      setShowTransferModal(true);
    }
  }, [isDisbursed, isLoadingStage, loanHistory]);

  const handleLogout = async () => {
    try {
      await squidloansUserAPI.logout();
      logout();
      window.location.href = "/";
    } catch (error) {
      console.error("Error during logout:", error);
      logout();
      window.location.href = "/";
    }
  };

  const handleRepay = async () => {
    try {
      setIsPaymentLoading(true);
      await initiateRazorpayPayment((result) => {
        if (result.success) {
          setPaymentDetails({
            amount: result.amount,
            order_id: result.order_id,
            payment_id: result.payment_id,
          });
          setShowPaymentSuccessModal(true);
        } else {
          showToast(result.error || "Payment failed");
        }
      });
    } catch (error) {
      showToast(error?.message || "Failed to initiate payment");
    } finally {
      setIsPaymentLoading(false);
    }
  };

  const handleReloanSubmit = async () => {

    try {
      setIsReloanLoading(true);
      // Call apply reloan API with user location
      const response = await squidloansUserAPI.applyReloan();

      if (response?.success && response?.data?.LEAD_STAGE) {
        const newLeadStage = response.data.LEAD_STAGE;
        const targetRoute = automationNodeLeadStageToRouteMap[newLeadStage];
        if (targetRoute) {
          navigate(targetRoute);
        } else {
          window.location.reload();
        }
      } else {
        window.location.reload();
      }
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong. Please try again.";
      showToast(errorMsg);
    } finally {
      setIsReloanLoading(false);
    }
  };

  if (isReloanLoading || isPaymentLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] flex flex-col justify-center items-center">
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 shadow-xl">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF6B35]"></div>
            <p className="mt-4 text-sm text-[#64748B]">Processing your reloan request...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative overflow-hidden flex flex-col">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -right-10 w-80 h-80 bg-[#FF6B35]/10 blur-[120px]"></div>
        <div className="absolute top-32 -left-10 w-72 h-72 bg-[#1E3A8A]/10 blur-[110px]"></div>
        <div className="absolute bottom-0 right-1/3 w-96 h-96 bg-[#FF6B35]/5 blur-[130px]"></div>
      </div>
      <div className="relative flex-1 flex flex-col pt-24 sm:pt-28 md:pt-32 pb-10 px-4 sm:px-6 lg:px-10">
        <div className="max-w-6xl w-full mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto px-4"
          >
            {/* Welcome Section */}
            <motion.div
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white border border-[#E2E8F0] rounded-3xl p-6 sm:p-8 mb-6 shadow-2xl shadow-[#FF6B35]/10 relative overflow-hidden mt-4"
            >
              <div className="relative">
                <div className="flex flex-col md:flex-row items-center justify-between mb-6">
                  <div className="flex items-center">
                    <div>
                      <h1 className="text-sm sm:text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-1 sm:mb-2">
                        Welcome back, {dashboardData.userName}!
                      </h1>
                      <p className="text-xs sm:text-sm text-[#64748B] mb-2 sm:mb-0">
                        looking decent, but SnapPaisa always blesses a boost!
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 w-full max-w-[220px]">
                    <div className="flex items-center gap-2 w-full">
                      {loanHistory[0]?.is_closed ? (
                        <motion.button
                          whileHover={{
                            scale: 1.05,
                            boxShadow: "0 10px 30px rgba(255, 107, 53, 0.3)",
                          }}
                          whileTap={{ scale: 0.95 }}
                          onClick={handleReloanSubmit}
                          className="px-3 py-2 rounded-xl font-medium flex items-center gap-2 flex-1 cursor-pointer bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white hover:opacity-90 transition-all shadow-md"
                        >
                          <FaWallet className="w-4 h-4" />
                          Reloan
                        </motion.button>
                      ) : isDisbursed ? (
                        <motion.button
                          whileHover={{
                            scale: 1.05,
                            boxShadow: "0 10px 30px rgba(255, 107, 53, 0.3)",
                          }}
                          whileTap={{ scale: 0.95 }}
                          onClick={handleRepay}
                          className="px-3 py-2 rounded-xl font-medium flex items-center gap-2 flex-1 cursor-pointer bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white hover:opacity-90 transition-all shadow-md"
                        >
                          <FaWallet className="w-4 h-4" />
                          Repay
                        </motion.button>
                      ) : (
                        <div className="flex-1" aria-hidden />
                      )}
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleLogout}
                        className="bg-red-600 text-white px-3 py-2 rounded-lg font-medium flex items-center gap-2 flex-1 cursor-pointer shadow-md hover:bg-red-700 hover:text-white"
                      >
                        <FaSignOutAlt className="w-4 h-4" />
                        Logout
                      </motion.button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 mb-6">
                  <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-4 flex flex-col justify-between">
                    <p className="text-sm text-[#64748B] font-medium">
                      Credit Score
                    </p>
                    <motion.p
                      className="text-xl font-bold text-[#FF6B35]"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5, delay: 1.5 }}
                    >
                      {dashboardData.creditScore}
                    </motion.p>
                    <div className="w-full bg-[#E2E8F0] rounded-full h-1.5 mt-2">
                      <motion.div
                        className="h-1.5 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#ff8c42]"
                        initial={{ width: "0%" }}
                        animate={{
                          width: `${(dashboardData.creditScore / 900) * 100}%`,
                        }}
                        transition={{
                          duration: 1.5,
                          ease: "easeOut",
                          delay: 0.2,
                        }}
                      />
                    </div>
                    <div className="flex justify-between mt-1 text-xs text-[#94A3B8]">
                      <span>300</span>
                      <span>900</span>
                    </div>
                    <div className="bg-white border border-[#E2E8F0] rounded-xl p-3 mt-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-gradient-to-br from-[#FF6B35] to-[#ff8c42]">
                          <svg
                            className="w-5 h-5 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M13 10V3L4 14h7v7l9-11h-7z"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#1E293B]">
                            Financial Tip of the Day
                          </p>
                          <p className="text-sm text-[#64748B] mt-1">
                            {financialTips[currentTip].tip}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Leads Grid */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="grid grid-cols-1 gap-6 mb-20"
            >
              {loanHistory.map((loan, index) => (
                <motion.div
                  key={loan.lead_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: 0.5 + index * 0.1,
                    ease: "easeOut",
                  }}
                  className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-2xl shadow-[#FF6B35]/10"
                >
                  <div className="flex flex-col">
                    {/* Header Section */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg sm:text-xl font-bold text-[#1E3A8A] break-all">
                          Loan #{loan.lead_no}
                        </h3>
                        <span
                          className={`px-2 py-1 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap ${loan.is_closed
                            ? "bg-green-50 text-green-700 border border-green-200"
                            : "bg-[#FF6B35] text-white"
                            }`}
                        >
                          {loan.is_closed ? "Closed" : "Active"}
                        </span>
                      </div>
                      {loan.sanction_letter_url && (
                        <motion.button
                          whileHover={{
                            scale: 1.02,
                            boxShadow: "0 10px 30px rgba(255, 107, 53, 0.3)",
                          }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() =>
                            window.open(loan.sanction_letter_url, "_blank")
                          }
                          className="px-3 py-1.5 rounded-xl font-medium flex items-center gap-2 text-sm bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white hover:opacity-90 transition-all shadow-md"
                        >
                          <FaFileDownload className="w-4 h-4" />
                          Download Sanction Letter
                        </motion.button>
                      )}
                    </div>

                    {/* Data Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
                        <p className="text-sm text-[#64748B] font-medium mb-1">
                          Loan Amount
                        </p>
                        <p className="text-xl font-bold text-[#FF6B35]">
                          ₹{loan.loan_amount}
                        </p>
                      </div>

                      {loan.sanction_details && (
                        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
                          <p className="text-sm text-[#64748B] font-medium mb-1">
                            Repayment Amount
                          </p>
                          <p className="text-xl font-bold text-[#FF6B35]">
                            ₹{loan.sanction_details.repayment_amount}
                          </p>
                        </div>
                      )}

                      {loan.disbursal_details?.disbursal_date && (
                        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
                          <p className="text-sm text-[#64748B] font-medium mb-1">
                            Disbursal Date
                          </p>
                          <p className="text-xl font-bold text-[#1E3A8A]">
                            {formatDate(loan.disbursal_details.disbursal_date)}
                          </p>
                        </div>
                      )}

                      {loan.sanction_details?.repayment_date && (
                        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
                          <p className="text-sm text-[#64748B] font-medium mb-1">
                            Repayment Date
                          </p>
                          <p className="text-xl font-bold text-[#1E3A8A]">
                            {formatDate(loan.sanction_details.repayment_date)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>


      {toast.show && (
        <Toast message={toast.message} type={toast.type} onClose={hideToast} />
      )}
      <GiftSuccessModal
        isOpen={showGiftSuccessModal || showCouponModal || showTransferModal}
        onClose={() => {
          setShowGiftSuccessModal(false);
          setShowCouponModal(false);
          setShowTransferModal(false);
        }}
        isCoupon={showCouponModal}
        isTransfer={showTransferModal}
        onRepay={async () => {
          try {
            await initiateRazorpayPayment((result) => {
              if (result.success) {
                setPaymentDetails({
                  amount: result.amount,
                  order_id: result.order_id,
                  payment_id: result.payment_id
                });
                setShowPaymentSuccessModal(true);
                setShowCouponModal(false);
              } else {
                showToast(result.error || 'Payment failed');
              }
            });
          } catch (error) {
            showToast(error.message);
          }
        }}
      />
      {showPaymentSuccessModal && (
        <PaymentSuccessModal
          isOpen={showPaymentSuccessModal}
          onClose={() => {
            setShowPaymentSuccessModal(false);
            setPaymentDetails(null);
            // Refresh the page to show updated loan status
            window.location.reload();
          }}
          paymentDetails={paymentDetails}
        />
      )}
    </div>
  );
}

export default Dashboard;
